import { useEffect, useId, useRef, useState } from "react";
import { api } from "../api.js";
import CreativesManager from "./creatives-manager/CreativesManager.jsx";
import PlatformRegistrationSettings from "../components/PlatformRegistrationSettings.jsx";
import PlatformZapierSettings from "../components/PlatformZapierSettings.jsx";
import PlatformBillingExemption from "../components/PlatformBillingExemption.jsx";
import Select from "../components/Select.jsx";
import "./PlatformManagement.css";

const ROOT = "/api/saas/platform";
const ROLES = ["owner", "admin", "marketer", "viewer"];
const ACTION_PATHS = {
  view: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></>,
  edit: <><path d="m15 4 5 5M4 20l5-1L20 8a3.5 3.5 0 0 0-5-5L4 14Z"/></>,
  assign: <><circle cx="9" cy="7" r="3"/><path d="M3 20v-2a6 6 0 0 1 12 0v2M19 8v8m-4-4h8"/></>,
  pause: <><path d="M8 5v14M16 5v14"/></>,
  activate: <><path d="m8 5 11 7-11 7Z"/></>,
  mail: <><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></>,
  recover: <><path d="M20 7a9 9 0 1 0 1 8M20 3v5h-5"/></>,
  cancel: <><circle cx="12" cy="12" r="9"/><path d="m9 9 6 6m0-6-6 6"/></>,
  remove: <><path d="M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7m4-7v7"/></>,
};
function ManagementAction({ icon, label: actionLabel, target, onClick, danger = false }) {
  return <button type="button" className="management-icon-action" title={actionLabel}
    aria-label={target ? `${actionLabel} for ${target}` : actionLabel} data-danger={danger} onClick={onClick}>
    <span aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{ACTION_PATHS[icon]}</svg></span>
  </button>;
}
function DirectoryActions({ row, view, platform, onOpen }) {
  const target = row.name || row.email;
  const editable = view === "companies" ? platform.capabilities?.includes("platform.companies.manage") : platform.capabilities?.includes("platform.users.manage") && !row.protected;
  if (view === "users" && row.status === "deleted") return <div className="management-row-actions">
    <ManagementAction icon="view" label="View details" target={target} onClick={() => onOpen("view")} />
    {editable && row.auth_sync_status !== "pending" && <ManagementAction icon="recover" label="Restore user" target={target} onClick={() => onOpen("restore")} />}
  </div>;
  return <div className="management-row-actions">
    <ManagementAction icon="view" label="View details" target={target} onClick={() => onOpen("view")} />
    {editable && <ManagementAction icon="edit" label="Edit details" target={target} onClick={() => onOpen("edit")} />}
      {!row.protected && platform.capabilities?.includes("platform.users.manage") && <ManagementAction icon="assign" label={view === "companies" ? "Invite user" : "Assign company"} target={target} onClick={() => onOpen("assign")} />}
      {view === "users" && editable && row.auth_sync_status !== "pending" && <ManagementAction icon="remove" label="Delete user" target={target} danger onClick={() => onOpen("delete")} />}
      {editable && row.auth_sync_status !== "pending" && <ManagementAction icon={row.status === "active" ? "pause" : "activate"}
        label={row.status === "active" ? (view === "companies" ? "Suspend company" : "Deactivate user") : (view === "companies" ? "Reactivate company" : "Reactivate user")}
        target={target} danger={row.status === "active"} onClick={() => onOpen("status")} />}
  </div>;
}

const label = (value) =>
  String(value || "Unknown")
    .replaceAll("_", " ")
    .replace(/^./, (c) => c.toUpperCase());
const date = (value) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";
const MANAGEMENT_ERRORS = {
  platform_last_owner: "Assign another active owner before making this change.",
  platform_user_deleted: "This user is deleted. Restore the user before changing their access.",
  platform_user_not_deleted: "This user is no longer deleted. Refresh the details before trying again.",
  platform_record_stale:
    "This record changed. Refresh the details and try again.",
  platform_invitation_conflict:
    "An active membership or pending invitation already exists.",
  platform_protected_identity:
    "Platform operator accounts are managed separately.",
  platform_invitation_not_pending: "This invitation is no longer pending.",
  platform_access_inactive:
    "Reactivate the company and user before assigning access.",
  platform_auth_sync_pending:
    "Retry authentication sync before changing account status.",
  platform_name_invalid: "Enter a valid display name.",
  platform_role_invalid: "Select Owner, Admin, Marketer or Viewer.",
  platform_email_invalid: "Enter a valid email address.",
  platform_url_invalid: "Enter a valid HTTPS URL.",
  platform_query_invalid: "Check the fields and filters.",
  company_name_conflict: "A company with this name already exists.",
  company_slug_conflict:
    "This company identifier is already in use. Choose another identifier.",
  owner_email_invalid: "Enter a valid owner email address.",
};
const message = (error) =>
  MANAGEMENT_ERRORS[error?.code] ||
  (error?.safeMessage && error.safeMessage !== "Request failed safely."
    ? error.safeMessage
    : "This request could not be completed. Please retry.");
export function ManagementStatus({ value }) {
  return (
    <span className="management-status" data-status={value}>
      {label(value)}
    </span>
  );
}
export function ManagementDialog({
  title,
  description,
  children,
  onClose,
  busy = false,
  wide = false,
}) {
  const dialogId = useId();
  const ref = useRef(null),
    closeRef = useRef(onClose);
  closeRef.current = busy ? null : onClose;
  useEffect(() => {
    const previous = document.activeElement;
    const dialog = ref.current;
    dialog?.querySelector("button")?.focus();
    const key = (event) => {
      if (
        [...document.querySelectorAll(".management-dialog")].at(-1) !==
          dialog ||
        document.querySelector(".platform-catalog-dialog-backdrop")
      )
        return;
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current?.();
      }
      if (event.key === "Tab") {
        const nodes = [
          ...dialog.querySelectorAll(
            'button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),a[href],[tabindex="0"]',
          ),
        ].filter((n) => n.getClientRects().length);
        const first = nodes[0],
          last = nodes.at(-1);
        if (!nodes.length) {
          event.preventDefault();
          dialog.focus();
        } else if (!dialog.contains(document.activeElement)) {
          event.preventDefault();
          (event.shiftKey ? last : first)?.focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("keydown", key);
      previous?.isConnected && previous.focus();
    };
  }, []);
  return (
    <div className="management-backdrop">
      <section
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${dialogId}-title`}
        aria-describedby={description ? `${dialogId}-description` : undefined}
        tabIndex={-1}
        className={`management-dialog ${wide ? "management-dialog-wide" : ""}`}
      >
        <header>
          <div>
            <span className="management-eyebrow">Platform management</span>
            <h2 id={`${dialogId}-title`}>{title}</h2>
          </div>
          <button
            type="button"
            aria-label="Close details"
            disabled={busy}
            onClick={onClose}
          >
            Close
          </button>
        </header>
        {description && <p id={`${dialogId}-description`}>{description}</p>}
        {children}
      </section>
    </div>
  );
}
function readQuery() {
  const q = new URLSearchParams(
    (typeof window === "undefined" ? "" : window.location.hash.split("?")[1]) ||
      "",
  );
  return {
    search: q.get("search") || "",
    status: q.get("status") || "",
    sort: q.get("sort") || "updated",
    limit: q.get("limit") || "25",
    offset: q.get("offset") || "0",
    role: q.get("role") || "",
    type: q.get("type") || "",
    mode: q.get("mode") || "users",
  };
}
export function useManagementQuery() {
  const [query, setQuery] = useState(readQuery);
  useEffect(() => {
    const onHash = () => setQuery(readQuery());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  // Merge into the current URL query, not this render's copy, so a delayed
  // change (a debounced search) cannot undo a filter chosen meanwhile.
  function change(patch) {
    const next = {
      ...readQuery(),
      ...patch,
      ...(!("offset" in patch) ? { offset: "0" } : {}),
    };
    const params = new URLSearchParams(
      Object.entries(next).filter(([, v]) => v !== ""),
    );
    window.location.hash = `${window.location.hash.split("?")[0]}?${params}`;
  }
  return [query, change];
}
// `keepData` keeps the last rows on screen while a refresh loads, so the
// focused control in a row survives (OGC-531 Creatives Manager).
export function useManagementData(path, revision = 0, { keepData = false } = {}) {
  const [state, setState] = useState({
    status: "loading",
    data: null,
    error: null,
  });
  const sequence = useRef(0);
  useEffect(() => {
    const current = ++sequence.current;
    const controller = new AbortController();
    setState((previous) => ({ status: "loading", data: keepData ? previous.data : null, error: null }));
    if (!path) return;
    api
      .get(path, { signal: controller.signal })
      .then((data) => {
        if (sequence.current === current)
          setState({ status: "ready", data, error: null });
      })
      .catch((error) => {
        if (sequence.current === current && !controller.signal.aborted)
          setState({ status: "error", data: null, error });
      });
    return () => {
      sequence.current++;
      controller.abort();
    };
  }, [path, revision]);
  return state;
}
function queryString(query) {
  return new URLSearchParams(
    Object.entries(query).filter(
      ([key, value]) => key !== "mode" && value !== "",
    ),
  ).toString();
}
export function ManagementPagination({ data, query, onChange, summary = null }) {
  const offset = Number(query.offset || 0),
    limit = Number(query.limit || 25),
    total = data?.total;
  return (
    <footer className="management-pagination">
      <span aria-live="polite">
        {summary ? summary : total == null
          ? "Loading results…"
          : total === 0
            ? "No results"
            : `${offset + 1}–${Math.min(offset + limit, total)} of ${total}`}
      </span>
      <label>
        Rows{" "}
        <Select
          aria-label="Rows per page"
          value={String(limit)}
          onChange={(value) => onChange({ limit: value })}
          options={[10, 25, 50].map((n) => ({ value: String(n), label: String(n) }))}
        />
      </label>
      <div>
        <button
          type="button"
          disabled={!offset || total == null}
          onClick={() =>
            onChange({ offset: String(Math.max(0, offset - limit)) })
          }
        >
          Previous
        </button>
        <button
          type="button"
          disabled={total == null || offset + limit >= total}
          onClick={() => onChange({ offset: String(offset + limit) })}
        >
          Next
        </button>
      </div>
    </footer>
  );
}
export function ManagementToolbar({
  query,
  onChange,
  statuses = [],
  children,
  onRefresh,
  showSort = true,
}) {
  const [search, setSearch] = useState(query.search || "");
  useEffect(() => setSearch(query.search || ""), [query.search]);
  return (
    <form
      className="management-toolbar"
      onSubmit={(e) => {
        e.preventDefault();
        onChange({ search });
      }}
    >
      <label className="management-search">
        <span className="sr-only">Search records</span>
        <input
          type="search"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </label>
      <button type="submit">Search</button>
      {statuses.length > 0 && (
        <label>
          <span className="sr-only">Filter by status</span>
          <Select
            aria-label="Filter by status"
            value={query.status || ""}
            onChange={(status) => onChange({ status })}
            options={[
              { value: "", label: "All statuses" },
              ...statuses.map((s) => ({ value: s, label: label(s) })),
            ]}
          />
        </label>
      )}
      {children}
      {showSort && (
        <Select
          aria-label="Sort records"
          value={query.sort || "updated"}
          onChange={(sort) => onChange({ sort })}
          options={[
            { value: "updated", label: "Recently updated" },
            { value: "name", label: "Name A–Z" },
          ]}
        />
      )}
      {onRefresh && (
        <button type="button" onClick={onRefresh}>
          Refresh
        </button>
      )}
    </form>
  );
}
function Feedback({ state, onRetry }) {
  if (state.status === "loading")
    return (
      <div className="management-empty" role="status">
        Loading records…
      </div>
    );
  if (state.error)
    return (
      <div className="management-error" role="alert">
        {message(state.error)}{" "}
        <button type="button" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  if (!state.data?.rows?.length)
    return (
      <div className="management-empty">
        No matching records. Try a different search or filter.
      </div>
    );
  return null;
}
function Field({
  label: fieldLabel,
  value,
  onChange,
  type = "text",
  required = false,
  maxLength = 160,
}) {
  return (
    <label className="management-field">
      {fieldLabel}
      <input
        type={type}
        value={value || ""}
        required={required}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
function RoleSelect({ value, onChange, label: fieldLabel = "Company role" }) {
  return (
    <label className="management-field">
      {fieldLabel}
      <Select
        className="bl-select-block"
        value={value}
        onChange={onChange}
        options={ROLES.map((role) => ({ value: role, label: label(role) }))}
      />
    </label>
  );
}
function CompanyPicker({ value, onChange }) {
  const [query, setQuery] = useState({ search: "", limit: "10", offset: "0" });
  const state = useManagementData(
    `${ROOT}/company-options?${queryString(query)}`,
  );
  return (
    <fieldset className="management-picker">
      <legend>Assign to company</legend>
      <div className="management-actions">
        <input
          aria-label="Search companies"
          placeholder="Search company names"
          value={query.search}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.preventDefault();
          }}
          onChange={(e) => {
            onChange("");
            setQuery({ search: e.target.value, limit: "10", offset: "0" });
          }}
        />
      </div>
      <Select
        className="bl-select-block"
        required
        aria-label="Company assignment"
        value={value}
        onChange={onChange}
        requiredMessage="Select a company."
        options={[
          { value: "", label: "Select a company" },
          ...(state.data?.rows || [])
            .filter((row) => row.status === "active")
            .map((row) => ({ value: row.id, label: row.name })),
        ]}
      />
      {state.error && <p role="alert">{message(state.error)}</p>}
      <ManagementPagination
        data={state.data}
        query={query}
        onChange={(patch) => {
          onChange("");
          setQuery((q) => ({
            ...q,
            ...patch,
            ...(!("offset" in patch) ? { offset: "0" } : {}),
          }));
        }}
      />
    </fieldset>
  );
}
function InviteForm({ companyId, email, onDone, onClose }) {
  const [form, setForm] = useState({
    companyId: companyId || "",
    email: email || "",
    role: "viewer",
    invitationId: crypto.randomUUID(),
    idempotencyKey: crypto.randomUUID(),
  });
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(null);
  const change = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: value,
      idempotencyKey: crypto.randomUUID(),
    }));
  return (
    <ManagementDialog
      title={email && !companyId ? "Assign company" : "Invite user"}
      description="They’ll receive an email to accept this company role and set up their account."
      onClose={() => !busy && onClose()}
      busy={busy}
    >
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (busy) return;
          setBusy(true);
          setError(null);
          try {
            const result = await api.post(`${ROOT}/user-invitations`, form);
            onDone(result);
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        }}
      >
        {!companyId && (
          <CompanyPicker
            value={form.companyId}
            onChange={(v) => change("companyId", v)}
          />
        )}
        <Field
          label="Email address"
          type="email"
          value={form.email}
          onChange={(v) => change("email", v)}
          required
          maxLength={320}
        />
        <RoleSelect value={form.role} onChange={(v) => change("role", v)} />
        {error && (
          <p className="management-error" role="alert">
            {message(error)}
          </p>
        )}
        <div className="management-form-actions">
          <button type="button" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            className="management-primary"
            disabled={busy || !form.companyId}
          >
            {busy ? "Sending…" : "Send invitation"}
          </button>
        </div>
      </form>
    </ManagementDialog>
  );
}
function CreateCompany({ onClose, onDone }) {
  const [form, setForm] = useState({
      name: "",
      slug: "",
      ownerEmail: "",
      idempotencyKey: crypto.randomUUID(),
    }),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(null);
  return (
    <ManagementDialog
      title="Create company"
      description="Create a clean workspace and invite its first owner."
      onClose={() => !busy && onClose()}
      busy={busy}
    >
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (busy) return;
          setBusy(true);
          setError(null);
          try {
            const result = await api.post(`${ROOT}/companies`, form, {
              headers: { "Idempotency-Key": form.idempotencyKey },
            });
            onDone(result);
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field
          label="Company name"
          value={form.name}
          required
          onChange={(name) =>
            setForm((f) => ({
              ...f,
              name,
              slug: name
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "")
                .slice(0, 80),
              idempotencyKey: crypto.randomUUID(),
            }))
          }
        />
        <Field
          label="Owner email"
          type="email"
          value={form.ownerEmail}
          required
          maxLength={320}
          onChange={(ownerEmail) =>
            setForm((f) => ({
              ...f,
              ownerEmail,
              idempotencyKey: crypto.randomUUID(),
            }))
          }
        />
        <details>
          <summary>Company identifier</summary>
          <Field
            label="Unique company slug"
            value={form.slug}
            required
            onChange={(slug) =>
              setForm((f) => ({
                ...f,
                slug,
                idempotencyKey: crypto.randomUUID(),
              }))
            }
          />
        </details>
        {error && (
          <p className="management-error" role="alert">
            {message(error)}
          </p>
        )}
        <div className="management-form-actions">
          <button type="button" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button className="management-primary" disabled={busy}>
            {busy ? "Creating…" : "Create company"}
          </button>
        </div>
      </form>
    </ManagementDialog>
  );
}
function Memberships({
  kind,
  target,
  canManage,
  canManageCompanies,
  onChanged,
}) {
  const [query, setQuery] = useState({ search: "", limit: "10", offset: "0" }),
    [revision, setRevision] = useState(0),
    [action, setAction] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(null);
  const state = useManagementData(
    `${ROOT}/${kind === "company" ? "companies" : "users"}/${target}/memberships?${queryString(query)}`,
    revision,
  );
  useEffect(() => {
    const total = state.data?.total;
    const offset = Number(query.offset), limit = Number(query.limit);
    if (total != null && offset > 0 && offset >= total)
      setQuery(current => ({...current, offset: String(Math.max(0, Math.ceil(total / limit) - 1) * limit)}));
  }, [state.data?.total, query.offset, query.limit]);
  async function applyMembershipChange() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { row, operation, role } = action;
      await api.post(`${ROOT}/memberships/${row.id}/${operation}`, {
        companyId: row.company_id,
        expectedUpdatedAt: row.updated_at,
        role,
      });
      setAction(null);
      setRevision((v) => v + 1);
      onChanged();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section>
      <h3>{kind === "company" ? "Members" : "Company assignments"}</h3>
      <ManagementToolbar
        showSort={false}
        query={{ ...query, sort: "updated" }}
        onChange={(patch) => setQuery((q) => ({ ...q, ...patch, offset: "0" }))}
      />
      <div className="management-table-wrap">
        <table className="management-table">
          <thead>
            <tr>
              <th>{kind === "company" ? "User" : "Company"}</th>
              <th>Role</th>
              <th>Status</th>
              <th className="management-actions-heading">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(state.data?.rows || []).map((row) => (
              <tr key={row.id}>
                <td data-label={kind === "company" ? "User" : "Company"}>
                  <strong>
                    {kind === "company"
                      ? row.name || row.email
                      : row.company_name}
                  </strong>
                  {kind === "company" && row.name && <small>{row.email}</small>}
                </td>
                <td data-label="Role">{label(row.role)}</td>
                <td data-label="Status">
                  <ManagementStatus value={row.status} />
                </td>
                <td data-label="Actions">
                  {canManage && !row.protected && row.status === "active" && (
                    <div className="management-row-actions">
                      <ManagementAction icon="edit" label="Change role" onClick={() => setAction({ row, operation: "role", role: row.role })} />
                      <ManagementAction icon="remove" label="Remove access" danger onClick={() => setAction({ row, operation: "remove" })} />
                    </div>
                  )}
                  {(canManage ||
                    (canManageCompanies && row.onboarding_owner)) &&
                    !row.protected &&
                    row.status === "invited" && (
                      <InvitationActions
                        row={row}
                        ownerOnly={!canManage}
                        onChanged={() => {
                          setRevision((v) => v + 1);
                          onChanged();
                        }}
                      />
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Feedback state={state} onRetry={() => setRevision((v) => v + 1)} />
      <ManagementPagination
        data={state.data}
        query={query}
        onChange={(patch) =>
          setQuery((q) => ({
            ...q,
            ...patch,
            ...(!("offset" in patch) ? { offset: "0" } : {}),
          }))
        }
      />
      {action && (
        <ManagementDialog
          title={
            action.operation === "role"
              ? "Change company role"
              : "Remove company access"
          }
          description={`This affects only ${action.row.company_name}. Account and activity history are retained.`}
          onClose={() => !busy && setAction(null)}
          busy={busy}
        >
          {action.operation === "role" && (
            <RoleSelect
              value={action.role}
              onChange={(role) => setAction((a) => ({ ...a, role }))}
            />
          )}{" "}
          {error && (
            <p role="alert" className="management-error">
              {message(error)}
            </p>
          )}
          <div className="management-form-actions">
            <button
              type="button"
              disabled={busy}
              onClick={() => setAction(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="management-primary"
              disabled={busy}
              onClick={applyMembershipChange}
            >
              {busy ? "Saving…" : "Confirm change"}
            </button>
          </div>
        </ManagementDialog>
      )}
    </section>
  );
}
function InvitationActions({ row, onChanged, ownerOnly = false }) {
  const [pending, setPending] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(null);
  async function run() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post(
        ownerOnly
          ? `${ROOT}/companies/${row.company_id}/owner-invitations/${row.id}/${pending.action}`
          : `${ROOT}/user-invitations/${row.id}/${pending.action}`,
        { companyId: row.company_id, idempotencyKey: pending.key },
      );
      setPending(null);
      onChanged(result);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <div className="management-row-actions">
        {["resend", "recover", "cancel"].map((action) => (
          <ManagementAction key={action} icon={action === "resend" ? "mail" : action}
            label={action === "resend" ? "Resend invite" : action === "recover" ? "Recover access" : "Cancel invite"}
            danger={action === "cancel"} onClick={() => setPending({ action, key: crypto.randomUUID() })} />
        ))}
      </div>
      {pending && (
        <ManagementDialog
          title={
            pending.action === "cancel"
              ? "Cancel invitation"
              : "Send access email"
          }
          description={
            pending.action === "cancel"
              ? `Cancel the pending invitation for ${row.email}.`
              : `Send ${pending.action === "recover" ? "an account recovery" : "a new invitation"} email to ${row.email}.`
          }
          onClose={() => !busy && setPending(null)}
          busy={busy}
        >
          {error && (
            <p role="alert" className="management-error">
              {message(error)}
            </p>
          )}
          <div className="management-form-actions">
            <button
              type="button"
              disabled={busy}
              onClick={() => setPending(null)}
            >
              Go back
            </button>
            <button
              type="button"
              className="management-primary"
              disabled={busy}
              onClick={run}
            >
              {busy ? "Working…" : "Confirm"}
            </button>
          </div>
        </ManagementDialog>
      )}
    </>
  );
}
function RecordDetails({ kind, id, platform, onClose, onChanged, initialAction }) {
  const initialActionStarted = useRef(false);
  const [revision, setRevision] = useState(0),
    [edit, setEdit] = useState(null),
    [action, setAction] = useState(null),
    [invite, setInvite] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(null);
  const path = `${ROOT}/${kind === "company" ? "companies" : "users"}/${id}`;
  const state = useManagementData(path, revision),
    record = state.data;
  const canUsers = platform.capabilities?.includes("platform.users.manage"),
    canEdit = kind === "company" ? platform.capabilities?.includes("platform.companies.manage") : (canUsers && !record?.protected);
  useEffect(() => {
    if (!record || initialActionStarted.current) return;
    initialActionStarted.current = true;
    if (kind === "user" && canEdit && record.auth_sync_status !== "pending" && ((initialAction === "delete" && record.status !== "deleted") || (initialAction === "restore" && record.status === "deleted"))) setAction(initialAction);
    if (initialAction === "edit" && canEdit && record.status !== "deleted") setEdit({name: record.name || "", privacyPolicyUrl: record.profile?.privacy_policy_url || "", followUpUrl: record.profile?.follow_up_url || "", profileRevision: record.profile?.revision || 0});
    if (initialAction === "assign" && canUsers && !record.protected && record.status !== "deleted") setInvite(true);
    if (initialAction === "status" && canEdit && record.status !== "deleted" && record.auth_sync_status !== "pending") setAction(record.status === "active" ? (kind === "company" ? "suspend" : "deactivate") : "reactivate");
  }, [record, initialAction, canEdit, canUsers, kind]);
  function refresh() {
    setRevision((v) => v + 1);
    onChanged();
  }
  async function save(operation, data = {}) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.post(`${path}/${operation}`, {
        expectedUpdatedAt:
          kind === "company" ? record.updated_at : record.updated_at,
        ...data,
      });
      setEdit(null);
      setAction(null);
      refresh();
      if (result.auth_sync_status === "pending")
        setError({
          safeMessage:
            "Access state saved. Authentication sync needs retry; use Retry access sync below. An in-flight attempt may take up to two minutes to become retryable.",
        });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <ManagementDialog
      title={record?.name || record?.email || "Details"}
      onClose={() => !busy && onClose()}
      busy={busy}
      wide
    >
      <Feedback
        state={{ ...state, data: record ? { rows: [record] } : null }}
        onRetry={refresh}
      />
      {record && (
        <>
          <div className="management-record-summary">
            <ManagementStatus value={record.status} />
            <span>{record.email || record.slug}</span>
            {record.protected && (
              <span>Platform operator · managed separately</span>
            )}
          </div>
          {error && (
            <p role="alert" className="management-error">
              {message(error)}
            </p>
          )}
          <div className="management-actions">
            {canEdit && record.status !== "deleted" && (
              <button
                type="button"
                onClick={() =>
                  setEdit({
                    name: record.name || "",
                    privacyPolicyUrl: record.profile?.privacy_policy_url || "",
                    followUpUrl: record.profile?.follow_up_url || "",
                    profileRevision: record.profile?.revision || 0,
                  })
                }
              >
                Edit details
              </button>
            )}
            {canUsers && !record.protected && record.status !== "deleted" && (
              <button type="button" onClick={() => setInvite(true)}>
                {kind === "company" ? "Invite user" : "Assign company"}
              </button>
            )}
            {canEdit && record.status !== "deleted" && (
              <button
                type="button"
                onClick={() =>
                  setAction(
                    kind === "company"
                      ? record.status === "active"
                        ? "suspend"
                        : "reactivate"
                      : record.status === "active"
                        ? "deactivate"
                        : "reactivate",
                  )
                }
              >
                {record.status === "active"
                  ? kind === "company"
                    ? "Suspend company"
                    : "Deactivate user"
                  : "Reactivate"}
              </button>
            )}
            {kind === "user" && canUsers && !record.protected && record.auth_sync_status !== "pending" && <button type="button" onClick={() => setAction(record.status === "deleted" ? "restore" : "delete")}>{record.status === "deleted" ? "Restore user" : "Delete user"}</button>}
            {canUsers &&
              !record.protected &&
              record.auth_sync_status === "pending" && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    save(
                      ["user.delete", "user.restore", "user.deactivate", "user.reactivate"].includes(record.auth_sync_action)
                        ? record.auth_sync_action.slice(5)
                        : record.status === "deleted" ? "delete" : record.status === "deactivated" ? "deactivate" : "reactivate",
                    )
                  }
                >
                  Retry access sync
                </button>
              )}
          </div>
          {kind === "company" && record.profile && (
            <dl className="management-profile">
              <dt>Privacy policy</dt>
              <dd>{record.profile.privacy_policy_url || "Not saved"}</dd>
              <dt>Follow-up page</dt>
              <dd>{record.profile.follow_up_url || "Not saved"}</dd>
            </dl>
          )}
          {kind === "company" && <PlatformBillingExemption platform={platform} companyId={id} companyName={record.name || "this company"} Dialog={ManagementDialog} onBusyChange={setBusy} />}
          <Memberships
            kind={kind}
            target={id}
            canManage={canUsers && record.status !== "deleted"}
            canManageCompanies={kind === "company"}
            onChanged={refresh}
          />
        </>
      )}
      {edit && (
        <ManagementDialog
          title="Edit details"
          onClose={() => !busy && setEdit(null)}
          busy={busy}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              save("edit", kind === "company" ? edit : { name: edit.name });
            }}
          >
            <Field
              label={kind === "company" ? "Company name" : "Display name"}
              value={edit.name}
              required
              maxLength={kind === "company" ? 160 : 120}
              onChange={(name) => setEdit((f) => ({ ...f, name }))}
            />
            {kind === "company" && (
              <>
                <Field
                  label="Privacy policy URL"
                  type="url"
                  value={edit.privacyPolicyUrl}
                  maxLength={2048}
                  onChange={(privacyPolicyUrl) =>
                    setEdit((f) => ({ ...f, privacyPolicyUrl }))
                  }
                />
                <Field
                  label="Follow-up page URL"
                  type="url"
                  value={edit.followUpUrl}
                  maxLength={2048}
                  onChange={(followUpUrl) =>
                    setEdit((f) => ({ ...f, followUpUrl }))
                  }
                />
                <p>
                  Saved URLs apply to future setup. Existing Meta Forms retain
                  their current URLs.
                </p>
              </>
            )}
            {error && (
              <p role="alert" className="management-error">
                {message(error)}
              </p>
            )}
            <div className="management-form-actions">
              <button
                type="button"
                disabled={busy}
                onClick={() => setEdit(null)}
              >
                Cancel
              </button>
              <button className="management-primary" disabled={busy}>
                Save changes
              </button>
            </div>
          </form>
        </ManagementDialog>
      )}
      {action && (
        <ManagementDialog
          title={`${label(action)} ${kind}`}
          description={
            kind === "company"
              ? action === "suspend"
                ? "Tenant access and new user actions will stop. Existing ads and provider activity will not be stopped. Company data and memberships are retained."
                : "Restore access for eligible members. This does not restore removed memberships or start ads."
              : action === "delete"
                ? "Remove this user from the normal user list and stop their access across all companies. History and memberships are retained. You can restore them using the Deleted filter."
                : action === "restore"
                  ? "Restore this user to the directory and allow a fresh sign-in. Removed company access stays removed."
                  : action === "deactivate"
                    ? "Stop this user’s tenant access across all companies. History and memberships are retained."
                    : "Restore account eligibility. A fresh sign-in is required; removed company access stays removed."
          }
          onClose={() => !busy && setAction(null)}
          busy={busy}
        >
          {error && (
            <p role="alert" className="management-error">
              {message(error)}
            </p>
          )}
          <div className="management-form-actions">
            <button
              type="button"
              disabled={busy}
              onClick={() => setAction(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="management-primary"
              disabled={busy}
              onClick={() => save(action)}
            >
              {busy ? "Saving…" : `Confirm ${action}`}
            </button>
          </div>
        </ManagementDialog>
      )}
      {invite && (
        <InviteForm
          companyId={kind === "company" ? id : null}
          email={kind === "user" ? record.email : null}
          onClose={() => setInvite(false)}
          onDone={() => {
            setInvite(false);
            refresh();
          }}
        />
      )}
    </ManagementDialog>
  );
}
export function CatalogThumbnail({ row }) {
  const ref = useRef(null),
    [url, setUrl] = useState(null);
  useEffect(() => {
    if (!row.version_id || !row.poster_asset_id) return;
    let live = true;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        observer.disconnect();
        api
          .get(
            `${ROOT}/catalog/versions/${row.version_id}/assets/poster/signed-url`,
          )
          .then((r) => {
            const value = r.preview?.url;
            if (live && /^https?:\/\//.test(value || "")) setUrl(value);
          })
          .catch(() => {});
      }
    });
    observer.observe(ref.current);
    return () => {
      live = false;
      observer.disconnect();
    };
  }, [row.version_id, row.poster_asset_id]);
  return (
    <span ref={ref} className="management-thumbnail">
      {url ? <img src={url} alt="" /> : <span aria-hidden="true">▶</span>}
    </span>
  );
}
export default function PlatformManagement({ view, session }) {
  const platform = session.user?.platform || {},
    identity = `${session.user?.id || session.user?.email}:${(platform.capabilities || []).join(",")}`;
  if (view === "catalog") return <CreativesManager key={identity} />;
  return (
    <ManagementWorkspace
      key={`${view}:${identity}`}
      view={view}
      platform={platform}
    />
  );
}
function ManagementWorkspace({ view, platform }) {
  const [query, change] = useManagementQuery(),
    [revision, setRevision] = useState(0),
    [selected, setSelected] = useState(null),
    [create, setCreate] = useState(false),
    [notice, setNotice] = useState("");
  const mode =
    view === "users" && query.mode === "invitations" ? "invitations" : view;
  const endpoint = mode === "invitations" ? "user-invitations" : view;
  const state = useManagementData(
    `${ROOT}/${endpoint}?${queryString(query)}`,
    revision,
  );
  const refresh = () => setRevision((v) => v + 1);
  const title = view === "companies" ? "Companies" : "Users";
  const canManage =
    view !== "users" ||
    platform.capabilities?.includes("platform.users.manage");
  useEffect(() => {
    const total = state.data?.total,
      offset = Number(query.offset),
      limit = Number(query.limit);
    if (total != null && offset > 0 && offset >= total)
      change({
        offset: String(Math.max(0, Math.ceil(total / limit) - 1) * limit),
      });
  }, [state.data?.total, query.offset, query.limit]);
  return (
    <section className="platform-management">
      <header className="management-page-heading">
        <div>
          <span className="management-eyebrow">Platform administration</span>
          <h1>{title}</h1>
          <p>
            {view === "companies"
              ? "Manage company details, owner access and availability."
              : "Manage tenant accounts and their company access."}
          </p>
        </div>
        {canManage && (
          <button
            className="management-primary"
            onClick={() => setCreate(true)}
          >
            {view === "companies" ? "Create company" : "Invite user"}{" "}
            <span aria-hidden="true">+</span>
          </button>
        )}
      </header>
      {view === "companies" && <PlatformRegistrationSettings platform={platform} />}
      {view === "companies" && <PlatformZapierSettings platform={platform} />}
      {notice && (
        <p className="management-notice" role="status">
          {notice}
        </p>
      )}
      {view === "users" && (
        <div className="management-segments" aria-label="User directory">
          <button
            aria-pressed={mode === "users"}
            onClick={() => change({ mode: "users", status: "" })}
          >
            Accounts
          </button>
          <button
            aria-pressed={mode === "invitations"}
            onClick={() => change({ mode: "invitations", status: "" })}
          >
            Invitations
          </button>
        </div>
      )}
      <ManagementToolbar
        query={query}
        onChange={change}
        onRefresh={refresh}
        statuses={
          view === "companies"
            ? ["active", "suspended"]
            : mode === "invitations"
              ? ["invited", "canceled"]
              : ["active", "deactivated", "deleted"]
        }
      >
        {view === "users" && (
          <Select
            aria-label="Filter by role"
            value={query.role}
            onChange={(role) => change({ role })}
            options={[
              { value: "", label: "All roles" },
              ...ROLES.map((r) => ({ value: r, label: label(r) })),
            ]}
          />
        )}
      </ManagementToolbar>
      <div className="management-panel">
        <div className="management-table-wrap">
          <table className="management-table">
            <thead>
              <tr>
                <th>
                  {view === "companies" ? "Company" : "Name / email"}
                </th>
                <th>Status</th>
                <th>
                  {view === "companies" ? "Owner access" : "Companies / role"}
                </th>
                <th>
                  {view === "companies" ? "Members" : "Updated"}
                </th>
                {view !== "users" && <th>Updated</th>}
                <th className="management-actions-heading">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(state.data?.rows || []).map((row) => (
                <tr key={row.id}>
                  <td
                    data-label={view === "companies" ? "Company" : "User"}
                  >
                    <div className="management-name">
                      <div>
                        <strong>{row.name || row.email}</strong>
                        {row.email && row.name && <small>{row.email}</small>}
                        {row.protected && view === "users" && (
                          <small>Platform operator</small>
                        )}
                      </div>
                    </div>
                  </td>
                  <td data-label="Status">
                    <ManagementStatus value={row.status} />
                    {mode === "invitations" && (
                      <small>
                        {row.invite_expires_at &&
                        Date.parse(row.invite_expires_at) < Date.now()
                          ? "Expired"
                          : label(row.invite_delivery_status)}
                      </small>
                    )}
                    {row.auth_sync_status === "pending" && (
                      <small>Access sync needs retry</small>
                    )}
                  </td>
                  <td
                    data-label={view === "companies" ? "Owner access" : "Companies / role"}
                  >
                    {view === "companies"
                      ? row.owners > 0
                        ? `${row.owners} active owner${row.owners === 1 ? "" : "s"}`
                        : row.pending_owner
                          ? "Invitation pending"
                          : "No active owner"
                      : mode === "invitations"
                        ? `${row.company_name} · ${label(row.role)}`
                        : `${row.company_count} compan${row.company_count === 1 ? "y" : "ies"}`}
                  </td>
                  <td
                    data-label={view === "companies" ? "Members" : "Updated"}
                  >
                    {view === "companies" ? row.members : date(row.updated_at)}
                  </td>
                  {view !== "users" && (
                    <td data-label="Updated">{date(row.updated_at)}</td>
                  )}
                  <td data-label="Actions">
                    {mode === "invitations" ? (
                      canManage &&
                      !row.protected &&
                      row.status === "invited" && (
                        <InvitationActions
                          row={row}
                          onChanged={(result) => {
                            refresh();
                            setNotice(
                              result?.deliveryStatus === "failed"
                                ? "Email delivery needs retry. The invitation remains pending."
                                : "Invitation updated.",
                            );
                          }}
                        />
                      )
                    ) : (
                      <DirectoryActions row={row} view={view} platform={platform} onOpen={(action) => setSelected({id: row.id, action})} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Feedback state={state} onRetry={refresh} />
        <ManagementPagination
          data={state.data}
          query={query}
          onChange={change}
        />
      </div>
      {selected && (
        <RecordDetails
          kind={view === "companies" ? "company" : "user"}
          key={`${selected.id}:${selected.action}`}
          id={selected.id}
          initialAction={selected.action}
          platform={platform}
          onClose={() => setSelected(null)}
          onChanged={refresh}
        />
      )}
      {create && view === "companies" && (
        <CreateCompany
          onClose={() => setCreate(false)}
          onDone={(result) => {
            setCreate(false);
            refresh();
            setNotice(
              result?.onboarding?.invitation?.deliveryStatus === "failed"
                ? "Company created. Owner email delivery needs retry."
                : "Company created. Review its owner invitation in details.",
            );
            setSelected(result?.onboarding?.company?.id ? {id: result.onboarding.company.id, action: "view"} : null);
          }}
        />
      )}
      {create && view === "users" && (
        <InviteForm
          onClose={() => setCreate(false)}
          onDone={(result) => {
            setCreate(false);
            refresh();
            setNotice(
              result.deliveryStatus === "sent"
                ? "Invitation sent. Access begins after acceptance."
                : "Invitation saved. Check delivery status in Invitations.",
            );
            change({ mode: "invitations", status: "", search: "" });
          }}
        />
      )}
    </section>
  );
}
